import React from 'react';
import { twMerge } from 'tailwind-merge';

interface LabelPlateProps {
    children: React.ReactNode;
    isCompact?: boolean;
    className?: string;
    testId?: string;
    ariaLabel?: string;
    /** When provided the plate renders as a real <button> for accessibility. */
    onClick?: () => void;
}

/** Icon size that fits inside a plate (status icons), by density. */
export const plateIconSize = (isCompact: boolean): string =>
    isCompact ? '0.875rem' : '1.25rem';

/**
 * Shared label "plate" badge: 24px tall (20px compact), 4px radius, 8px×2px
 * padding. The name plate and every status-icon plate build on this so they
 * stay visually identical. Clickable plates render as a native <button> (which
 * handles focus + Enter/Space) rather than a div with a "button" role.
 */
const LabelPlate: React.FC<LabelPlateProps> = ({
    children,
    isCompact = false,
    className,
    testId,
    ariaLabel,
    onClick,
}) => {
    const classes = twMerge(
        'flex items-center justify-center rounded bg-gray-900/80 text-xs font-semibold',
        isCompact ? 'h-5 px-1.5 py-0.5' : 'h-6 px-2 py-0.5',
        className,
    );

    if (onClick) {
        return (
            <button
                type="button"
                className={classes}
                data-test-id={testId}
                aria-label={ariaLabel}
                onClick={onClick}
            >
                {children}
            </button>
        );
    }

    return (
        <div className={classes} data-test-id={testId} aria-label={ariaLabel}>
            {children}
        </div>
    );
};

export default LabelPlate;
